import {
    GOOGLE_COUNTRIES,
    INSTAGRAM_COUNTRIES,
    TIKTOK_COUNTRIES,
} from 'config/constants';
import {
    formatAbsoluteGrowth,
    formatGrowth,
    formatPercentage,
    formatUnixTimestampWeek,
    formatVolume,
} from './helpers';
import { tooltips, views } from '../copy';

export const DATA_SOURCES = {
    GOOGLE: {
        key: 'GOOGLE',
        label: 'Google',
        supportedGeos: GOOGLE_COUNTRIES,
    },
    TIKTOK: {
        key: 'TIKTOK',
        label: 'TikTok',
        supportedGeos: TIKTOK_COUNTRIES,
    },
    INSTAGRAM: {
        key: 'INSTAGRAM',
        label: 'Instagram',
        supportedGeos: INSTAGRAM_COUNTRIES,
    },
    POPULARITY: {
        key: 'POPULARITY',
        label: 'Popularity',
        supportedGeos: TIKTOK_COUNTRIES,
    },
};

export const NUMERICAL_NOTATION_TYPES = {
    SIGNED: 'SIGNED',
    PERCENTAGE: 'PERCENTAGE',
    UNSIGNED: 'UNSIGNED',
    PERCENTAGE_UNSIGNED: 'PERCENTAGE_UNSIGNED',
    UNIX_TIMESTAMP_WEEK: 'UNIX_TIMESTAMP_WEEK',
};

const STRING_NOTATION_TYPES = {
    STRING: 'STRING',
};

const formatFnsByNotationType = {
    [NUMERICAL_NOTATION_TYPES.SIGNED]: formatAbsoluteGrowth,
    [NUMERICAL_NOTATION_TYPES.PERCENTAGE]: formatGrowth,
    [NUMERICAL_NOTATION_TYPES.UNSIGNED]: formatVolume,
    [NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED]: formatPercentage,
    [NUMERICAL_NOTATION_TYPES.UNIX_TIMESTAMP_WEEK]: formatUnixTimestampWeek,
    [STRING_NOTATION_TYPES.STRING]: (value) => value,
};

const getFormatFn = (notationType) => {
    if (notationType in formatFnsByNotationType) {
        return formatFnsByNotationType[notationType];
    }
    throw new Error(`Unsupported notation type: ${notationType}`);
};

const DEFAULT_TABLE_COLUMN_WIDTH_MIN = 5;
const DEFAULT_TABLE_COLUMN_WIDTH = '8.625rem';

export class SpateMetricMetadata {
    constructor(config) {
        if (!config.key) throw new Error('Missing key in metric config');
        if (!config.tooltipType)
            console.warn(
                `Missing tooltipType in metric config for key: ${config.key}`,
            );
        if (!config.mixpanelContentKey)
            console.warn(
                `Missing mixpanelContentKey in metric config for key: ${config.key}`,
            );

        this.config = config;
        this.key = config.key;
        this.tooltipType = config.tooltipType;
        this.mixpanelContentKey = config.mixpanelContentKey;

        this.tableColumnWidthMin =
            config.tableColumnWidthMin || DEFAULT_TABLE_COLUMN_WIDTH_MIN;
        this.tableColumnWidth =
            config.tableColumnWidth || DEFAULT_TABLE_COLUMN_WIDTH;
        this.tableHeadColumnLabel = config.tableHeadColumnLabel || config.key;
        this.userFacingKeyDesc =
            config.userFacingKeyDesc ||
            `${this.tableHeadColumnLabel.toLowerCase()} ${views.descending}`;
        this.userFacingKeyAsc =
            config.userFacingKeyAsc ||
            `${this.tableHeadColumnLabel.toLowerCase()} ${views.ascending}`;
        this.notation = config.notation || NUMERICAL_NOTATION_TYPES.UNSIGNED;
        this.healthStatKey = config.healthStatKey;
        this.activeHealthStatLabel = Boolean(config.activeHealthStatLabel);
        this.dataSource = config.dataSource?.key;

        this.format = getFormatFn(this.notation);
    }

    getSortOption = ({ ascending = false }) => {
        return {
            sortBy: this.key,
            label: this.tableHeadColumnLabel,
            format: this.format,
            ascending,
            value: ascending ? this.userFacingKeyAsc : this.userFacingKeyDesc,
            tooltip: tooltips[this.tooltipType],
            mixpanelContentKey: this.mixpanelContentKey,
            dataSource: this.dataSource,
            isSigned: [
                NUMERICAL_NOTATION_TYPES.SIGNED,
                NUMERICAL_NOTATION_TYPES.PERCENTAGE,
            ].includes(this.notation),
        };
    };

    getTableColumnConfig = (overrides = {}) => {
        return {
            key: this.key,
            label: this.tableHeadColumnLabel,
            $minWidth: this.tableColumnWidthMin,
            $width: this.tableColumnWidth,
            tooltip: tooltips[this.tooltipType],
            mixpanelContentKey: this.mixpanelContentKey,
            ...overrides,
        };
    };
}

const GOOGLE_METADATA_CONFIG = [
    {
        key: 'volume',
        tableHeadColumnLabel: 'Volume',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'volume',
        mixpanelContentKey: 'dataTableVolume',
        tableColumnWidthMin: 8.4,
        healthStatKey: 'average monthly searches',
    },
    {
        key: 'brandsVolume',
        userFacingKeyDesc: `Brands Volume ${views.descending}`,
        userFacingKeyAsc: `Brands Volume ${views.ascending}`,
        tableHeadColumnLabel: 'Brands Volume',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'brandsVolume',
        mixpanelContentKey: 'dataTableBrandsVolume',
        tableColumnWidthMin: 8.4,
        healthStatKey: 'average brand searches',
    },
    {
        key: 'MoM',
        userFacingKeyDesc: `MoM ${views.descending}`,
        userFacingKeyAsc: `MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'growthMoM',
        mixpanelContentKey: 'dataTableGrowthMoM',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'MoM_increase',
        tableHeadColumnLabel: 'increase MoM',
        userFacingKeyDesc: `MoM increase ${views.descending}`,
        userFacingKeyAsc: `MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'growthMoMAbsolute',
        mixpanelContentKey: 'dataTableGrowthMoMAbsolute',
        tableColumnWidthMin: 8.4,
    },
    {
        key: 'QoQ',
        userFacingKeyDesc: `QoQ ${views.descending}`,
        userFacingKeyAsc: `QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'growthQoQ',
        mixpanelContentKey: 'dataTableGrowthQoQ',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'QoQ_increase',
        tableHeadColumnLabel: 'increase QoQ',
        userFacingKeyDesc: `QoQ increase ${views.descending}`,
        userFacingKeyAsc: `QoQ increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'growthQoQAbsolute',
        mixpanelContentKey: 'dataTableGrowthQoQAbsolute',
        tableColumnWidthMin: 8.4,
    },
    {
        key: 'YoY',
        userFacingKeyDesc: `YoY ${views.descending}`,
        userFacingKeyAsc: `YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'growth',
        mixpanelContentKey: 'dataTableGrowth',
        tableColumnWidthMin: 6,
    },
    {
        key: 'absoluteGrowth',
        tableHeadColumnLabel: 'increase YoY',
        userFacingKeyDesc: `YoY increase ${views.descending}`,
        userFacingKeyAsc: `YoY increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'growthAbsolute',
        mixpanelContentKey: 'dataTableGrowthAbsolute',
        tableColumnWidthMin: 8.4,
    },
    {
        key: 'predictedYoY',
        tableHeadColumnLabel: 'Predicted YoY',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'predictedGrowth',
        mixpanelContentKey: 'dataTablePredictedGrowth',
        healthStatKey: 'predicted growth',
        tableColumnWidthMin: 8,
    },
    {
        key: 'price_segmentation',
        tableHeadColumnLabel: 'Price',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'pricePoint',
        mixpanelContentKey: 'dataTablePricePoint',
        healthStatKey: 'price point',
        tableColumnWidthMin: 8.625,
    },
];

const TIKTOK_METADATA_CONFIG = [
    {
        key: 'views',
        tableHeadColumnLabel: 'Total Views',
        userFacingKeyDesc: `views: all time ${views.descending}`,
        userFacingKeyAsc: `views: all time ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'views',
        mixpanelContentKey: 'dataTableViews',
        tableColumnWidthMin: 6,
    },
    {
        key: 'views_MoM_increase',
        tableHeadColumnLabel: 'Total Views MoM Increase',
        userFacingKeyDesc: `views: all time MoM increase ${views.descending}`,
        userFacingKeyAsc: `views: all time MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'views_MoM_increase',
        mixpanelContentKey: 'dataTableViewsMoMIncrease',
    },
    {
        key: 'views_MoM',
        tableHeadColumnLabel: 'Total Views MoM',
        userFacingKeyDesc: `views: all time MoM ${views.descending}`,
        userFacingKeyAsc: `views: all time MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'views_MoM',
        mixpanelContentKey: 'dataTableViewsMoM',
    },
    {
        key: 'views_WoW',
        tableHeadColumnLabel: 'WoW',
        userFacingKeyDesc: `views: all time WoW ${views.descending}`,
        userFacingKeyAsc: `views: all time WoW ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'views_WoW',
        mixpanelContentKey: 'dataTableViewsWoW',
    },
    {
        key: 'views_WoW_increase',
        tableHeadColumnLabel: 'Increase',
        userFacingKeyDesc: `views: all time WoW increase ${views.descending}`,
        userFacingKeyAsc: `views: all time WoW increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'views_WoW_increase',
        mixpanelContentKey: 'dataTableViewsWoWIncrease',
    },
    {
        key: 'non_cum_avg_weekly_views',
        tableHeadColumnLabel: 'Views',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'non_cum_avg_weekly_views',
        mixpanelContentKey: 'dataTableViews',
        tableColumnWidthMin: 6,
        healthStatKey: 'average weekly views',
    },
    {
        key: 'non_cum_increase_MoM',
        tableHeadColumnLabel: 'Increase MoM',
        userFacingKeyDesc: `views MoM increase ${views.descending}`,
        userFacingKeyAsc: `views MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'non_cum_increase_MoM',
        mixpanelContentKey: 'dataTableViewsMoMIncrease',
    },
    {
        key: 'non_cum_MoM',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `views MoM ${views.descending}`,
        userFacingKeyAsc: `views MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'non_cum_MoM',
        mixpanelContentKey: 'dataTableViewsMoM',
    },
    {
        key: 'non_cum_increase_WoW',
        tableHeadColumnLabel: 'Increase WoW',
        userFacingKeyDesc: `views WoW increase ${views.descending}`,
        userFacingKeyAsc: `views WoW increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'non_cum_increase_WoW',
        mixpanelContentKey: 'dataTableViewsWoWIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_WoW',
        tableHeadColumnLabel: 'WoW',
        userFacingKeyDesc: `views WoW ${views.descending}`,
        userFacingKeyAsc: `views WoW ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'non_cum_WoW',
        mixpanelContentKey: 'dataTableViewsWoW',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_increase_WoW_rolling',
        tableHeadColumnLabel: 'Increase WoW',
        userFacingKeyDesc: `views WoW increase ${views.descending}`,
        userFacingKeyAsc: `views WoW increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'non_cum_increase_WoW',
        mixpanelContentKey: 'dataTableViewsWoWIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_WoW_rolling',
        tableHeadColumnLabel: 'WoW',
        userFacingKeyDesc: `views WoW ${views.descending}`,
        userFacingKeyAsc: `views WoW ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'non_cum_WoW',
        mixpanelContentKey: 'dataTableViewsWoW',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'total_posts_cnt',
        tableHeadColumnLabel: 'Total Posts',
        userFacingKeyDesc: `posts ${views.descending}`,
        userFacingKeyAsc: `posts ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'total_posts_cnt',
        mixpanelContentKey: 'postsCount',
        tableColumnWidthMin: 5.5,
        healthStatKey: 'posts',
    },
    {
        key: 'paid_posts_views_prc',
        tableHeadColumnLabel: 'Paid',
        userFacingKeyDesc: `paid views ${views.descending}`,
        userFacingKeyAsc: `paid views ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'paid_posts_views_prc',
        mixpanelContentKey: 'paidViews',
        tableColumnWidthMin: 5.125,
    },
    {
        key: 'paid_posts_views_prc_all',
        tableHeadColumnLabel: 'Paid: All Time',
        userFacingKeyDesc: `paid views ${views.descending}`,
        userFacingKeyAsc: `paid views ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'paid_posts_views_prc_all',
        mixpanelContentKey: 'paidViewsAll',
        healthStatKey: 'paid views: all time',
        tableColumnWidthMin: 8.125,
    },
    {
        key: 'partner_paid_partnership_posts_views_prc',
        tableHeadColumnLabel: 'Paid',
        userFacingKeyDesc: `paid views ${views.descending}`,
        userFacingKeyAsc: `paid views ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'partner_paid_partnership_posts_views_prc',
        mixpanelContentKey: 'paidViews',
        tableColumnWidthMin: 5.125,
        healthStatKey: 'paid views',
    },
    {
        key: 'eligible_for_commission_posts_views_prc',
        tableHeadColumnLabel: 'TikTok Shop',
        userFacingKeyDesc: `shop views ${views.descending}`,
        userFacingKeyAsc: `shop views ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'eligible_for_commission_posts_views_prc',
        mixpanelContentKey: 'shopViews',
        tableColumnWidthMin: 6.5,
        healthStatKey: 'TikTok shop',
    },
    {
        key: 'eligible_for_commission_posts_views_prc_all',
        tableHeadColumnLabel: 'TikTok Shop: All Time',
        userFacingKeyDesc: `shop views: all time ${views.descending}`,
        userFacingKeyAsc: `shop views: all time ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'eligible_for_commission_posts_views_prc_all',
        mixpanelContentKey: 'shopViewsAll',
        tableColumnWidthMin: 10.125,
        healthStatKey: 'TikTok shop: all time',
    },
    {
        key: 'last_week_views',
        tableHeadColumnLabel: 'Views: Last Week',
        userFacingKeyDesc: `views: last week ${views.descending}`,
        userFacingKeyAsc: `views: last week ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'last week views',
        mixpanelContentKey: 'topPostViewsLastWeek',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'total_post_views',
        tableHeadColumnLabel: 'Total Views',
        userFacingKeyDesc: `post views: all time ${views.descending}`,
        userFacingKeyAsc: `post views: all time ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'total post views',
        mixpanelContentKey: 'topPostViewsTotal',
        // Wide enough for the label to fit beside the sort icons.
        tableColumnWidthMin: 6,
    },
    {
        key: 'avg_weekly_views',
        tableHeadColumnLabel: 'Avg Weekly Views',
        userFacingKeyDesc: `post avg weekly views ${views.descending}`,
        userFacingKeyAsc: `post avg weekly views ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'avg weekly views',
        mixpanelContentKey: 'topPostViewsAvgWeekly',
        // Wide enough for the label to fit beside the sort icons.
        tableColumnWidthMin: 9,
    },
    {
        key: 'non_cum_increase_YoY_rolling',
        tableHeadColumnLabel: 'increase YoY',
        userFacingKeyDesc: `views YoY increase ${views.descending}`,
        userFacingKeyAsc: `views YoY increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'non_cum_increase_YoY_rolling',
        mixpanelContentKey: 'dataTableViewsYoYIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_YoY_rolling',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `views YoY ${views.descending}`,
        userFacingKeyAsc: `views YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'non_cum_YoY_rolling',
        mixpanelContentKey: 'dataTableViewsYoY',
        tableColumnWidthMin: 6,
    },
    {
        key: 'non_cum_increase_QoQ_rolling',
        tableHeadColumnLabel: 'increase QoQ',
        userFacingKeyDesc: `views QoQ increase ${views.descending}`,
        userFacingKeyAsc: `views QoQ increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'non_cum_increase_QoQ_rolling',
        mixpanelContentKey: 'dataTableViewsQoQIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_QoQ_rolling',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `views QoQ ${views.descending}`,
        userFacingKeyAsc: `views QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'non_cum_QoQ_rolling',
        mixpanelContentKey: 'dataTableViewsQoQ',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_increase_MoM_rolling',
        tableHeadColumnLabel: 'increase MoM',
        userFacingKeyDesc: `views MoM increase ${views.descending}`,
        userFacingKeyAsc: `views MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'non_cum_increase_MoM_rolling',
        mixpanelContentKey: 'dataTableViewsMoMIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_MoM_rolling',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `views MoM ${views.descending}`,
        userFacingKeyAsc: `views MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'non_cum_MoM_rolling',
        mixpanelContentKey: 'dataTableViewsMoM',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'engagement_prc',
        tableHeadColumnLabel: 'engagement',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'engagement_prc',
        mixpanelContentKey: 'dataTableEngagement',
        tableColumnWidthMin: 8,
        healthStatKey: 'engagement',
    },
    {
        key: 'engagement_prc_all',
        tableHeadColumnLabel: 'Engagement: All Time',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'engagement_prc',
        mixpanelContentKey: 'dataTableEngagement',
        tableColumnWidthMin: 10,
        healthStatKey: 'engagement: all time',
    },
    {
        key: 'nb_likes_last_week',
        tableHeadColumnLabel: 'Likes: Latest Week',
        tooltipType: 'nb_likes_last_week',
        mixpanelContentKey: 'nbLikesLastWeek',
        tableColumnWidthMin: 10,
    },
    {
        key: 'nb_likes_all',
        tableHeadColumnLabel: 'Likes: All Time',
        tooltipType: 'nb_likes_all',
        mixpanelContentKey: 'nbLikesAllTime',
        tableColumnWidthMin: 10,
    },
    {
        key: 'nb_views_last_week',
        tableHeadColumnLabel: 'Views: Latest Week',
        tooltipType: 'nb_views_last_week',
        mixpanelContentKey: 'nbViewsLastWeek',
        tableColumnWidthMin: 10,
    },
    {
        key: 'nb_views_all',
        tableHeadColumnLabel: 'Views: All Time',
        tooltipType: 'nb_views_all',
        mixpanelContentKey: 'nbViewsAll',
        tableColumnWidthMin: 10,
    },
    {
        key: 'sentiment_score',
        tableHeadColumnLabel: 'Sentiment',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'sentiment_score',
        mixpanelContentKey: 'sentimentScore',
        tableColumnWidthMin: 8,
        healthStatKey: 'sentiment score',
    },
    {
        key: 'create_timestamp',
        tableHeadColumnLabel: 'Posted',
        notation: NUMERICAL_NOTATION_TYPES.UNIX_TIMESTAMP_WEEK,
        mixpanelContentKey: 'posted',
        // Wide enough for the label to fit beside the sort icons.
        tableColumnWidthMin: 6.5,
    },
];

const INSTAGRAM_METADATA_CONFIG = [
    {
        key: 'likes',
        tableHeadColumnLabel: 'Total Likes',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'totalPosts',
        mixpanelContentKey: 'dataTableInstagramTotalPosts',
        tableColumnWidthMin: 5.5,
        healthStatKey: 'ig posts',
    },
    {
        key: 'non_cum_increase_MoM_rolling',
        tableHeadColumnLabel: 'Increase MoM',
        userFacingKeyDesc: `likes MoM increase ${views.descending}`,
        userFacingKeyAsc: `likes MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramPostsMoMIncrease',
        mixpanelContentKey: 'dataTableInstagramPostsMoMIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_MoM_rolling',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `likes MoM ${views.descending}`,
        userFacingKeyAsc: `likes MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPostsMoM',
        mixpanelContentKey: 'dataTableInstagramPostsMoM',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_increase_QoQ_rolling',
        tableHeadColumnLabel: 'Increase QoQ',
        userFacingKeyDesc: `likes QoQ increase ${views.descending}`,
        userFacingKeyAsc: `likes QoQ increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramPostsQoQIncrease',
        mixpanelContentKey: 'dataTableInstagramPostsQoQIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_QoQ_rolling',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `likes QoQ ${views.descending}`,
        userFacingKeyAsc: `likes QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPostsQoQ',
        mixpanelContentKey: 'dataTableInstagramPostsQoQ',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_increase_WoW',
        tableHeadColumnLabel: 'Increase WoW',
        userFacingKeyDesc: `likes WoW increase ${views.descending}`,
        userFacingKeyAsc: `likes WoW increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramPostsWoWIncrease',
        mixpanelContentKey: 'dataTableInstagramPostsWoWIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_WoW',
        tableHeadColumnLabel: 'WoW',
        userFacingKeyDesc: `likes WoW ${views.descending}`,
        userFacingKeyAsc: `likes WoW ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPostsWoW',
        mixpanelContentKey: 'dataTableInstagramPostsWoW',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_increase_YoY_rolling',
        tableHeadColumnLabel: 'Increase YoY',
        userFacingKeyDesc: `likes YoY increase ${views.descending}`,
        userFacingKeyAsc: `likes YoY increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramPostsYoYIncrease',
        mixpanelContentKey: 'dataTableInstagramPostsYoYIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_YoY_rolling',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `likes YoY ${views.descending}`,
        userFacingKeyAsc: `likes YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPostsYoY',
        mixpanelContentKey: 'dataTableInstagramPostsYoY',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_avg_weekly_likes',
        tableHeadColumnLabel: 'Likes',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'avgWeeklyLikes',
        mixpanelContentKey: 'dataTableInstagramAvgWeeklyLikes',
        tableColumnWidthMin: 6,
        healthStatKey: 'average weekly likes',
    },
    {
        key: 'non_cum_likes_increase_MoM_rolling',
        tableHeadColumnLabel: 'Increase MoM',
        userFacingKeyDesc: `likes MoM increase ${views.descending}`,
        userFacingKeyAsc: `likes MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramLikesMoMIncrease',
        mixpanelContentKey: 'dataTableInstagramLikesMoMIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_likes_MoM_rolling',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `likes MoM ${views.descending}`,
        userFacingKeyAsc: `likes MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramLikesMoM',
        mixpanelContentKey: 'dataTableInstagramLikesMoM',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_likes_increase_QoQ_rolling',
        tableHeadColumnLabel: 'Increase QoQ',
        userFacingKeyDesc: `likes QoQ increase ${views.descending}`,
        userFacingKeyAsc: `likes QoQ increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramLikesQoQIncrease',
        mixpanelContentKey: 'dataTableInstagramLikesQoQIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_likes_QoQ_rolling',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `likes QoQ ${views.descending}`,
        userFacingKeyAsc: `likes QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramLikesQoQ',
        mixpanelContentKey: 'dataTableInstagramLikesQoQ',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_likes_increase_WoW_rolling',
        tableHeadColumnLabel: 'Increase WoW',
        userFacingKeyDesc: `likes WoW increase ${views.descending}`,
        userFacingKeyAsc: `likes WoW increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramLikesWoWIncrease',
        mixpanelContentKey: 'dataTableInstagramLikesWoWIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_likes_WoW_rolling',
        tableHeadColumnLabel: 'WoW',
        userFacingKeyDesc: `likes WoW ${views.descending}`,
        userFacingKeyAsc: `likes WoW ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramLikesWoW',
        mixpanelContentKey: 'dataTableInstagramPostsWoW',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'non_cum_likes_increase_YoY_rolling',
        tableHeadColumnLabel: 'Increase YoY',
        userFacingKeyDesc: `likes YoY increase ${views.descending}`,
        userFacingKeyAsc: `likes YoY increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'instagramLikesYoYIncrease',
        mixpanelContentKey: 'dataTableInstagramLikesYoYIncrease',
        tableColumnWidthMin: 7.25,
    },
    {
        key: 'non_cum_likes_YoY_rolling',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `likes YoY ${views.descending}`,
        userFacingKeyAsc: `likes YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramLikesYoY',
        mixpanelContentKey: 'dataTableInstagramLikesYoY',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'last_week_likes',
        tableHeadColumnLabel: 'Likes: Last Week',
        userFacingKeyDesc: `post likes: last week ${views.descending}`,
        userFacingKeyAsc: `post likes: last week ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'instagram last week likes',
        mixpanelContentKey: 'topPostLikesLastWeek',
    },
    {
        key: 'total_post_likes',
        tableHeadColumnLabel: 'Total Likes',
        userFacingKeyDesc: `post likes: all time ${views.descending}`,
        userFacingKeyAsc: `post likes: all time ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'instagram total post likes',
        mixpanelContentKey: 'topPostLikesTotal',
    },
    {
        key: 'avg_weekly_likes',
        tableHeadColumnLabel: 'Avg Weekly Likes',
        userFacingKeyDesc: `post avg weekly likes ${views.descending}`,
        userFacingKeyAsc: `post avg weekly likes ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'instagram avg weekly likes',
        mixpanelContentKey: 'topPostLikesAvgWeekly',
    },
    {
        key: 'wow',
        tableHeadColumnLabel: 'WoW',
        userFacingKeyDesc: `likes WoW ${views.descending}`,
        userFacingKeyAsc: `likes WoW ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramLikesWoW',
        mixpanelContentKey: 'dataTableInstagramPostsWoW',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'create_timestamp',
        tableHeadColumnLabel: 'Posted',
        notation: NUMERICAL_NOTATION_TYPES.UNIX_TIMESTAMP_WEEK,
        mixpanelContentKey: 'posted',
        tableColumnWidthMin: 2.5,
    },
];

const POPULARITY_METADATA_CONFIG = [
    {
        key: 'popularity_index_avg_12_month',
        tableHeadColumnLabel: 'Popularity',
        notation: NUMERICAL_NOTATION_TYPES.UNSIGNED,
        tooltipType: 'averageMonthlyPopularityOneYear',
        mixpanelContentKey: 'dataTablePopularityIndex',
        tableColumnWidthMin: 8.5,
    },
    {
        key: 'search_YoY',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `Google Popularity YoY ${views.descending}`,
        userFacingKeyAsc: `Google Popularity YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'googlePopularityYoY',
        mixpanelContentKey: 'dataTableGooglePopularityYoY',
        dataSource: DATA_SOURCES.GOOGLE,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'search_QoQ',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `Google Popularity QoQ ${views.descending}`,
        userFacingKeyAsc: `Google Popularity QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'googlePopularityQoQ',
        mixpanelContentKey: 'dataTableGooglePopularityQoQ',
        dataSource: DATA_SOURCES.GOOGLE,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'search_MoM',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `Google Popularity MoM ${views.descending}`,
        userFacingKeyAsc: `Google Popularity MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'googlePopularityMoM',
        mixpanelContentKey: 'dataTableGooglePopularityMoM',
        dataSource: DATA_SOURCES.GOOGLE,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'tiktok_YoY',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `TikTok Popularity YoY ${views.descending}`,
        userFacingKeyAsc: `TikTok Popularity YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'tikTokPopularityYoY',
        mixpanelContentKey: 'dataTableTikTokPopularityYoY',
        dataSource: DATA_SOURCES.TIKTOK,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'tiktok_QoQ',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `TikTok Popularity QoQ ${views.descending}`,
        userFacingKeyAsc: `TikTok Popularity QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'tikTokPopularityQoQ',
        mixpanelContentKey: 'dataTableTikTokPopularityQoQ',
        dataSource: DATA_SOURCES.TIKTOK,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'tiktok_MoM',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `TikTok Popularity MoM ${views.descending}`,
        userFacingKeyAsc: `TikTok Popularity MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'tikTokPopularityMoM',
        mixpanelContentKey: 'dataTableTikTokPopularityMoM',
        dataSource: DATA_SOURCES.TIKTOK,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'instagram_YoY',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `Instagram Popularity YoY ${views.descending}`,
        userFacingKeyAsc: `Instagram Popularity YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPopularityYoY',
        mixpanelContentKey: 'dataTableInstagramPopularityYoY',
        dataSource: DATA_SOURCES.INSTAGRAM,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'instagram_QoQ',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `Instagram Popularity QoQ ${views.descending}`,
        userFacingKeyAsc: `Instagram Popularity QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPopularityQoQ',
        mixpanelContentKey: 'dataTableInstagramPopularityQoQ',
        dataSource: DATA_SOURCES.INSTAGRAM,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'instagram_MoM',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `Instagram Popularity MoM ${views.descending}`,
        userFacingKeyAsc: `Instagram Popularity MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'instagramPopularityMoM',
        mixpanelContentKey: 'dataTableInstagramPopularityMoM',
        dataSource: DATA_SOURCES.INSTAGRAM,
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'YoY',
        tableHeadColumnLabel: 'YoY',
        userFacingKeyDesc: `Popularity YoY ${views.descending}`,
        userFacingKeyAsc: `Popularity YoY ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'popularityYoY',
        mixpanelContentKey: 'dataTablePopularityYoY',
        tableColumnWidthMin: 5.5,
    },
    {
        key: 'QoQ',
        tableHeadColumnLabel: 'QoQ',
        userFacingKeyDesc: `Popularity QoQ ${views.descending}`,
        userFacingKeyAsc: `Popularity QoQ ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'popularityQoQ',
        mixpanelContentKey: 'dataTablePopularityQoQ',
        tableColumnWidthMin: 6,
    },
    {
        key: 'MoM',
        tableHeadColumnLabel: 'MoM',
        userFacingKeyDesc: `Popularity MoM ${views.descending}`,
        userFacingKeyAsc: `Popularity MoM ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'popularityMoM',
        mixpanelContentKey: 'dataTablePopularityMoM',
        tableColumnWidthMin: 6,
    },
    {
        key: 'increase_YoY',
        tableHeadColumnLabel: 'Increase YoY',
        userFacingKeyDesc: `Popularity YoY increase ${views.descending}`,
        userFacingKeyAsc: `Popularity YoY increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'popularityIncreaseYoY',
        mixpanelContentKey: 'dataTablePopularityIncreaseYoY',
        tableColumnWidthMin: 7.5,
    },
    {
        key: 'increase_QoQ',
        tableHeadColumnLabel: 'Increase QoQ',
        userFacingKeyDesc: `Popularity QoQ increase ${views.descending}`,
        userFacingKeyAsc: `Popularity QoQ increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'popularityIncreaseQoQ',
        mixpanelContentKey: 'dataTablePopularityIncreaseQoQ',
        tableColumnWidthMin: 7.5,
    },
    {
        key: 'increase_MoM',
        tableHeadColumnLabel: 'Increase MoM',
        userFacingKeyDesc: `Popularity MoM increase ${views.descending}`,
        userFacingKeyAsc: `Popularity MoM increase ${views.ascending}`,
        notation: NUMERICAL_NOTATION_TYPES.SIGNED,
        tooltipType: 'popularityIncreaseMoM',
        mixpanelContentKey: 'dataTablePopularityIncreaseMoM',
        tableColumnWidthMin: 7.5,
    },
    {
        key: 'predicted_YoY',
        tableHeadColumnLabel: 'Predicted YoY',
        userFacingKeyAsc: `Popularity Predicted YoY ${views.ascending}`,
        userFacingKeyDesc: `Popularity Predicted YoY ${views.descending}`,
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE,
        tooltipType: 'popularityPredictedYoY',
        mixpanelContentKey: 'dataTablePopularityPredictedYoY',
        tableColumnWidthMin: 8,
        healthStatKey: 'popularity predicted growth',
    },
    {
        key: 'convergence_prc',
        tableHeadColumnLabel: 'convergence',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tooltipType: 'convergence_prc',
        mixpanelContentKey: 'dataTableConvergencePercentage',
        tableColumnWidthMin: 8,
        healthStatKey: 'popularity convergence',
    },
    {
        key: 'google_share_prc',
        tableHeadColumnLabel: 'Google Share',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tableColumnWidthMin: 6.75,
    },
    {
        key: 'tiktok_share_prc',
        tableHeadColumnLabel: 'TikTok Share',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tableColumnWidthMin: 6.5,
    },
    {
        key: 'instagram_share_prc',
        tableHeadColumnLabel: 'Instagram Share',
        notation: NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
        tableColumnWidthMin: 7.5,
    },
];

const byKey = (arr) =>
    arr.reduce((acc, obj) => {
        acc[obj.key] = obj;
        return acc;
    }, {});

export const GOOGLE_METADATA_BY_KEY = byKey(
    GOOGLE_METADATA_CONFIG.map(
        (config) =>
            new SpateMetricMetadata({
                ...config,
                dataSource: DATA_SOURCES.GOOGLE,
            }),
    ),
);
export const INSTAGRAM_METADATA_BY_KEY = byKey(
    INSTAGRAM_METADATA_CONFIG.map(
        (config) =>
            new SpateMetricMetadata({
                ...config,
                dataSource: DATA_SOURCES.INSTAGRAM,
            }),
    ),
);
export const TIKTOK_METADATA_BY_KEY = byKey(
    TIKTOK_METADATA_CONFIG.map(
        (config) =>
            new SpateMetricMetadata({
                ...config,
                dataSource: DATA_SOURCES.TIKTOK,
            }),
    ),
);
export const POPULARITY_METADATA_BY_KEY = byKey(
    POPULARITY_METADATA_CONFIG.map(
        (config) => new SpateMetricMetadata({ ...config }),
    ),
);

const HEALTH_STAT_TOOLTIP_SUPPLEMENTAL = {
    'average monthly popularity': 'averageMonthlyPopularityOneYear',
};

export const HEALTH_STAT_TOOLTIP_TYPE_BY_KEY = [
    ...GOOGLE_METADATA_CONFIG,
    ...TIKTOK_METADATA_CONFIG,
    ...INSTAGRAM_METADATA_CONFIG,
    ...POPULARITY_METADATA_CONFIG,
].reduce(
    (acc, config) => {
        if (config.healthStatKey && config.tooltipType)
            acc[config.healthStatKey] = config.tooltipType;
        return acc;
    },
    { ...HEALTH_STAT_TOOLTIP_SUPPLEMENTAL },
);

export const getHealthStatDefinition = (healthStatKey) => {
    const tooltipType = HEALTH_STAT_TOOLTIP_TYPE_BY_KEY[healthStatKey];
    return tooltipType ? tooltips[tooltipType] : null;
};

const reduceMetadataToSortOptions = (acc, [key, metric]) => {
    acc[key] = metric.getSortOption({ ascending: false });
    acc[`${key}_asc`] = metric.getSortOption({ ascending: true });
    return acc;
};

export const TIKTOK_SORT_OPTIONS = Object.entries(
    TIKTOK_METADATA_BY_KEY,
).reduce(reduceMetadataToSortOptions, {});
export const GOOGLE_SORT_OPTIONS = Object.entries(
    GOOGLE_METADATA_BY_KEY,
).reduce(reduceMetadataToSortOptions, {});
export const INSTAGRAM_SORT_OPTIONS = Object.entries(
    INSTAGRAM_METADATA_BY_KEY,
).reduce(reduceMetadataToSortOptions, {});
export const POPULARITY_SORT_OPTIONS = Object.entries(
    POPULARITY_METADATA_BY_KEY,
).reduce(reduceMetadataToSortOptions, {});
